import {
  Avatar,
  Box,
  Button,
  Flex,
  Text,
  Icon,
  Input,
  FormLabel,
  Checkbox,
  Image,
  Skeleton,
} from "@chakra-ui/react";
import { DateRangePicker } from "react-date-range";
import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { FetchCohortEventsReport } from "./action";
import { RootReducer } from "../../../RootReducer";
import { columnsDataComplex } from "./variables/columnsData";
import ComplexTable from "./components/ComplexTable";
import { Select } from "chakra-react-select";
import {
  FetchOverviewReport,
} from "../default/action";
import { useUserPermissions } from "hooks/useUserPermissions";

const useOutsideClick = (callback: (ranges: any) => void, ranges: any) => {
  const ref = useRef<any>();

  useEffect(() => {
    const handleClick = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback(ranges);
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [ref, ranges]);

  return ref;
};

const testGroupOptions = [
  {
    label: "New",
    value: "0",
  },
  {
    label: "Old",
    value: "1",
  },
];

const clientOptions = [
  {
    label: "PLAYTIME",
    value: "pro.cashtree",
  },
  {
    label: "Supreme King Android",
    value: "io.purrcat",
  },
  {
    label: "MONEY CASH",
    value: "io.playtime",
  },
  {
    label: "CASH BUDDY",
    value: "io.gametime",
  },
  {
    label: "TM Android",
    value: "gametester.io",
  },
  {
    label: "TM iOS",
    value: "app.testmax",
  },
  {
    label: "Tester Buddy",
    value: "io.buddy",
  },
  {
    label: "Supreme King iOS",
    value: "com.bird.mon",
  },
  {
    label: "PLAYOUT",
    value: "io.playout",
  },
];

const sourceOptions = [
  {
    label: "Organic",
    value: "organic",
  },
  {
    label: "Google Ads",
    value: "Google Ads",
  },
  {
    label: "Motive Interactive",
    value: "Motive Interactive",
  },
  {
    label: "Motive Interactive Secondary",
    value: "Motive Interactive Secondary",
  },
  {
    label: "Unity Ads",
    value: "Unity Ads",
  },
  {
    label: "Gamelight",
    value: "Gamelight",
  },
  {
    label: "Facebook",
    value: "Facebook",
  },
  {
    label: "Snapchat",
    value: "Snapchat",
  },
  {
    label: "TikTok",
    value: "TikTok For Business",
  },
  {
    label: "MyAppFree",
    value: "MyAppFree",
  },
  {
    label: "ironSource",
    value: "ironSource",
  },
  {
    label: "ironSource Aura",
    value: "ironSource Aura",
  },
  {
    label: "Tapjoy",
    value: "Tapjoy",
  },
  {
    label: "AppLabs",
    value: "AppLabs",
  },
  {
    label: "AdAction Interactive CPE",
    value: "AdAction Interactive CPE",
  },
  {
    label: "Appluck",
    value: "Appluck",
  },
  {
    label: "Apple Search Ads",
    value: "Apple Search Ads",
  },
  {
    label: "Applovin",
    value: "Applovin",
  },
  {
    label: "Digital Turbine",
    value: "Digital Turbine",
  },
  {
    label: "Moloco",
    value: "Moloco",
  },
  {
    label: "Prodege",
    value: "Prodege",
  },
  {
    label: "Skyflag",
    value: "Skyflag",
  },
  {
    label: "Hang My Ads",
    value: "Hang My Ads",
  },
  {
    label: "Zucks Affiliate",
    value: "Zucks Affiliate",
  },
  {
    label: "Zucks CPE",
    value: "Zucks CPE",
  },
  {
    label: "Liftoff",
    value: "Liftoff",
  },
];

export default function Events() {
  const { isSpecialMode } = useUserPermissions();

  const dispatch = useDispatch();

  const reportRequestState = useSelector(
    (state: RootReducer) => state.events.fetchCohortReportRequestState
  );
  const report = useSelector((state: RootReducer) => state.events.report);

  const overviewReport = useSelector(
    (state: RootReducer) => state.overview.report
  );

  const campaigns = overviewReport ? overviewReport.campaigns : [];

  const [selectedApp, setSelectedApp] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedSource, setSelectedSource] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedClient, setSelectedClient] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedTestGroup, setSelectedTestGroup] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [uniquePerUser, setUniquePerUser] = useState<boolean>(false);

  const showExtra = window.location.search === "?show=extra";

  let appOptions: any = [];
  let campaignOptions: any = [];

  campaigns.forEach((c) => {
    appOptions.push({
      label: c.appName,
      value: c.appId,
    });
  });

  appOptions = [
    ...new Map(appOptions.map((item: any) => [item["value"], item])).values(),
  ];

  campaigns.forEach((c) => {
    if (selectedApp && selectedApp.value === c.appId) {
      campaignOptions.push({
        label: c.name,
        value: c.id,
      });
    }
  });

  const isReportSuccessful = reportRequestState.isSuccessful;

  const [ranges, setRanges] = useState({
    startDate: moment().toDate(),
    endDate: moment().toDate(),
    key: "selection",
  });
  const [internalRanges, setInternalRanges] = useState({
    startDate: moment().toDate(),
    endDate: moment().toDate(),
    key: "selection",
  });

  useEffect(() => {
    if (!selectedApp) {
      return;
    }

    const filter = {
      campaignId: selectedCampaign ? selectedCampaign.value : null,
      source: selectedSource ? selectedSource.value : null,
      testGroup: selectedTestGroup ? selectedTestGroup.value : null,
      packageName: selectedApp ? selectedApp.value : null,
      client: selectedClient ? selectedClient.value : null,
    };

    dispatch(
      FetchCohortEventsReport(
        ranges.startDate,
        ranges.endDate,
        uniquePerUser,
        filter
      )
    );
  }, [
    ranges.startDate,
    ranges.endDate,
    selectedCampaign,
    selectedSource,
    selectedApp,
    selectedClient,
    selectedTestGroup,
    uniquePerUser,
    dispatch,
  ]);

  useEffect(() => {
    if (!selectedApp && appOptions && appOptions.length > 0) {
      setSelectedApp(appOptions[0]);
    }
  }, [appOptions, selectedApp]);

  useEffect(() => {
    dispatch(FetchOverviewReport(ranges.startDate, ranges.endDate, "all"));
  }, [dispatch]);

  const [isDatePickerVisible, setDatePickerVisible] = useState(false);

  const handleClickOutside = useCallback(
    (internalRanges: any) => {
      if (isDatePickerVisible) {
        setRanges(internalRanges);
        setDatePickerVisible(false);
      }
    },
    [isDatePickerVisible]
  );

  const ref = useOutsideClick(handleClickOutside, internalRanges);

  const differentDates =
    internalRanges.startDate.toDateString() ==
    internalRanges.endDate.toDateString()
      ? 1
      : 2;
  const formattedDate =
    differentDates === 1
      ? moment(internalRanges.startDate).format("ll")
      : `${moment(internalRanges.startDate).format("ll")} - ${moment(
          internalRanges.endDate
        ).format("ll")}`;

  const selectedC = campaigns.find((c) => {
    if (!selectedApp) {
      return;
    }

    return c.appId === selectedApp.value;
  });

  if (!isSpecialMode) {
    return <></>;
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Flex alignItems={"flex-start"}>
        <Flex>
          {selectedC?.iconUrl ? (
            <Image
              referrerPolicy="no-referrer"
              height={140}
              width={140}
              borderRadius={4}
              marginRight={4}
              src={selectedC.iconUrl}
            />
          ) : (
            <Flex
              bgColor={"gray.200"}
              height={140}
              width={140}
              borderRadius={4}
              marginRight={4}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Skeleton
                height={140}
                width={140}
                borderRadius={4}
                pos="absolute"
                zIndex={1}
                opacity={0.1}
              />
              <Text
                color={"gray.700"}
                fontSize={"xs"}
                fontWeight="medium"
                zIndex={100}
                textAlign={"center"}
                mx={2}
              >
                Please select an app and a campaign.
              </Text>
            </Flex>
          )}

          <Flex flexDirection={"column"}>
            <div
              style={{
                width: 500,
                height: 40,
                backgroundColor: "white",
                borderRadius: 14,
                marginBottom: 15,
              }}
            >
              <Select
                value={selectedApp}
                isMulti={false}
                placeholder={"Select an App"}
                onChange={(v) => {
                  setSelectedApp(v);
                  setSelectedCampaign(null);
                }}
                isClearable={false}
                //@ts-ignore
                options={appOptions}
                //selectedOptionColor='red'
              />
            </div>
            <div
              style={{
                width: 500,
                height: 40,
                backgroundColor: "white",
                borderRadius: 14,
                marginBottom: 15,
              }}
            >
              <Select
                value={selectedCampaign}
                isMulti={false}
                placeholder={"Select a Campaign"}
                onChange={(v) => {
                  setSelectedCampaign(v);
                }}
                isClearable={true}
                //@ts-ignore
                options={campaignOptions}
                //selectedOptionColor='red'
              />
            </div>
            {showExtra && (
              <div
                style={{
                  width: 500,
                  height: 40,
                  backgroundColor: "white",
                  borderRadius: 14,
                  marginBottom: 15,
                }}
              >
                <Select
                  value={selectedSource}
                  isMulti={false}
                  placeholder={"Select a Source"}
                  onChange={(v) => {
                    setSelectedSource(v);
                  }}
                  isClearable={true}
                  //@ts-ignore
                  options={sourceOptions}
                  //selectedOptionColor='red'
                />
              </div>
            )}
            {showExtra && (
              <div
                style={{
                  width: 500,
                  height: 40,
                  backgroundColor: "white",
                  borderRadius: 14,
                  marginBottom: 15,
                }}
              >
                <Select
                  value={selectedClient}
                  isMulti={false}
                  placeholder={"Select a Package"}
                  onChange={(v) => {
                    setSelectedClient(v);
                  }}
                  isClearable={true}
                  //@ts-ignore
                  options={clientOptions}
                  //selectedOptionColor='red'
                />
              </div>
            )}
            {showExtra && (
              <div
                style={{
                  width: 500,
                  height: 40,
                  backgroundColor: "white",
                  borderRadius: 14,
                }}
              >
                <Select
                  value={selectedTestGroup}
                  isMulti={false}
                  placeholder={"Select a Test Group"}
                  onChange={(v) => {
                    setSelectedTestGroup(v);
                  }}
                  isClearable={true}
                  //@ts-ignore
                  options={testGroupOptions}
                  //selectedOptionColor='red'
                />
              </div>
            )}
          </Flex>
        </Flex>
        <Box
          top={{
            base: 32,
            xl: 28,
          }}
          ref={ref}
          style={{ position: "absolute", right: 30 }}
        >
          <Input
            borderRadius={16}
            onClick={() => {
              setInternalRanges(ranges);
              setDatePickerVisible(!isDatePickerVisible);
            }}
            style={{
              backgroundColor: "white",
              textAlign: "center",
              width: differentDates * 120 + 40,
            }}
            defaultValue={formattedDate}
            value={formattedDate}
          />

          {isDatePickerVisible && (
            <Box
              style={{
                position: "absolute",
                right: 20,
                zIndex: 200,
                top: 50,
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
              }}
            >
              <DateRangePicker
                ranges={[internalRanges]}
                onChange={(newRanges: any) => {
                  setInternalRanges({
                    endDate: newRanges.selection.endDate,
                    key: newRanges.selection.key,
                    startDate: newRanges.selection.startDate,
                  });
                }}
              />
            </Box>
          )}
        </Box>
        {showExtra && (
          <Flex
            paddingX={4}
            height={"40px"}
            marginX={4}
            borderRadius={8}
            alignItems="center"
            backgroundColor={"white"}
          >
            <Checkbox
              isChecked={uniquePerUser}
              onChange={(e) => setUniquePerUser(e.target.checked)}
              fontWeight={"medium"}
              colorScheme="purple"
            >
              Unique Per User
            </Checkbox>
          </Flex>
        )}
      </Flex>
      <ComplexTable
        showExtra={showExtra}
        isLoading={!isReportSuccessful}
        columns={columnsDataComplex}
        tableData={report ? report.rows : []}
      />
    </Box>
  );
}
